import { ClassValue } from 'clsx';
import { Text } from '@shared/components/Text';
import { DateField } from '@shared/data/entity/fields/DateField';
import { formatDate, formatTime } from '@shared/util/date';
import { CollectionViewFieldContainer } from './CollectionViewFieldContainer';
import { FieldVariant } from './FieldVariant';
import { getDateText } from './util/getDateText';

interface Props {
  variant: FieldVariant;
  className?: ClassValue;
  field: DateField;
}

/**
 * Default: the date and time with the year, on one line. Compact: the date (the year
 * only when it isn't this one) and the time as two parts, the time dimmed, laid out by the host.
 * Inline: as text.
 */
export function DateCollectionViewField({ field, variant, className }: Props) {
  if (field.isEmpty) {
    return null;
  }

  const isInline = variant === 'inline';
  const isCompact = variant === 'compact';
  const isDefault = variant === 'default';

  return (
    <CollectionViewFieldContainer
      fieldType={field.fieldType}
      variant={variant}
      className={className}
    >
      {isInline && <InlineDate field={field} />}
      {isCompact && <CompactDate field={field} />}
      {isDefault && <DefaultDate field={field} />}
    </CollectionViewFieldContainer>
  );
}

function InlineDate({ field }: { field: DateField }) {
  return <>{getDateText(field)}</>;
}

function CompactDate({ field }: { field: DateField }) {
  return (
    <>
      <Text numberOfLines={1}>{formatDate(field.date)}</Text>
      <Text weight="normal" inactive numberOfLines={1}>
        {formatTime(field.date)}
      </Text>
    </>
  );
}

function DefaultDate({ field }: { field: DateField }) {
  return <Text numberOfLines={1}>{field.formattedDate}</Text>;
}
